import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { NewInstance, inject, Factory } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { debounce } from 'app/shared/lib/decorators';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AppLogger } from '../lib/logging/app-logger';

type CustomEventHandler = (...args: any[]) => any;

@inject(NewInstance.of(EventAggregator), AppLogger)
export class TableConfiguration implements ITableConfiguration {

    public readonly name: string = '';
    public order: string[] = [];
    public hidden: string[] = [];
    public locked: string[] = [];

    protected readonly events!: EventAggregator;
    protected readonly subscriptions!: Map<string, Map<CustomEventHandler, Subscription>>;
    protected readonly logger!: AppLogger;

    protected dispatching = false;

    constructor(events: EventAggregator, logger: AppLogger, name: string, config?: ITableConfiguration) {
        this.name = name;
        Reflect.defineProperty(this, 'events', { writable: false, enumerable: false, value: events });
        Reflect.defineProperty(this, 'subscriptions', { writable: false, enumerable: false, value: new Map() });
        Reflect.defineProperty(this, 'logger', { writable: false, enumerable: false, value: logger });
        if(config) {
            Object.assign(this, config);
        }
    }

    public addEventListener(name: string, handler: (...args: any[]) => any) {
        const handlerMap = this.subscriptions.get(name) || new Map<CustomEventHandler, Subscription>();
        handlerMap.set(handler, this.events.subscribe(name, handler));
        this.subscriptions.set(name, handlerMap);
    }

    public removeEventListener(name: string, handler: (...args: any[]) => any) {
        const handlerMap = this.subscriptions.get(name);
        const subscription = handlerMap?.get(handler);
        return subscription?.dispose();
    }

    public dispatchEvent(name: string, event?: any) {
        if(this.dispatching) {
            // this.logger.warn(`Infinite recursion detected in table with key "${this.name}". Are two different tables using the same key? Consider explicitly assigning a distinct key property on the <ess-table>.`);
            return;
        }
        try {
            this.dispatching = true;
            this.events.publish(name, event);
        }

        finally {
            this.dispatching = false;
        }
    }
}

@inject(Api, EnvironmentService, Factory.of(TableConfiguration))
export class TableConfigurationService {

    protected userConfig: Record<string, IUserTableConfiguration> = {};
    protected adminConfig: Record<string, ITableConfiguration> = {};
    protected currentUserConfig: Record<string, TableConfiguration> = {};

    constructor(protected api: Api, protected env: EnvironmentService, protected tableConfigurationFactory: (name: string, config?: ITableConfiguration) => TableConfiguration) {
        this.loadFromApi();
    }

    public getTableConfiguration(table: string): TableConfiguration {
        this.currentUserConfig[table] = this.currentUserConfig[table] || this.tableConfigurationFactory(table);
        return this.currentUserConfig[table];
    }

    public getUserTableConfiguration(table: string): TableConfiguration {
        this.loadFromApi(); // trigger reconciliation between the admin and user configs
        const config = this.currentUserConfig[table] || this.tableConfigurationFactory(table);
        return config;
    }

    public getAdminTableConfiguration(table: string): TableConfiguration {
        const config = this.currentUserConfig[table] || this.tableConfigurationFactory(table);
        Object.assign(config, this.adminConfig[table]);
        return config;
    }

    public revertTableConfiguration(table: string): TableConfiguration {
        const config = this.currentUserConfig[table] || this.tableConfigurationFactory(table);
        Object.assign(config, this.adminConfig[table]);
        config.dispatchEvent('tempupdate'); // this event triggers only the config menu to reconfigure (Reset has been clicked, but not Apply)
        return config;
    }

    public async updateTableConfiguration(table: string, config: Partial<ITableConfiguration>) {
        const currentUser = this.currentUserConfig[table] = this.currentUserConfig[table] || this.tableConfigurationFactory(table);
        Object.assign(currentUser, config);
        currentUser.dispatchEvent('update'); // this event triggers the table to reconfigure
        await this.syncUserConfig();
    }

    public async updateTableAdminConfiguration(table: string, config: ITableConfiguration) {
        this.adminConfig[table] = config;
        await this.syncAdminConfig();
    }

    protected async loadFromApi() {
        await Promise.all([
            this.api.get(this.api.storedObject('app-admin', 'ui/tables/admin.json'))
                .catch(err => {
                    if(err.httpStatusCode === 404) {
                        return {};
                    }
                    throw err;
                })
                .then((adminConfig) => this.adminConfig = adminConfig),
            this.api.get(this.api.storedObject('app-user', 'ui/tables/settings.json'))
                .catch(err => {
                    if(err.httpStatusCode === 404) {
                        return {};
                    }
                    throw err;
                })
                .then((userConfig) => this.userConfig = userConfig)
        ]);
        this.generateEffectiveConfigurations();
    }

    protected generateEffectiveConfigurations() {
        const tables = new Set([...Reflect.ownKeys(this.adminConfig) as string[], ...Reflect.ownKeys(this.userConfig) as string[]]);
        tables.forEach((table) => {
            const userConfig = this.userConfig[table] || { order: [], hidden: [] };
            const adminConfig = this.adminConfig[table] || { order: [], hidden: [], locked: [] };
            const config = {
                order: userConfig.order || adminConfig.order,
                hidden: userConfig.hidden

                    // remove columns locked visible
                    .filter((col) => adminConfig.hidden?.includes(col) || !adminConfig.locked?.includes(col))

                    // include columns locked hidden
                    .concat(adminConfig.locked?.filter((col) => adminConfig.hidden?.includes(col)) || []),
                locked: adminConfig.locked
            };
            const current = this.currentUserConfig[table] || this.tableConfigurationFactory(table);
            Object.assign(current, config);
            current.dispatchEvent('update');
        });
    }

    @debounce(250)
    protected async syncUserConfig() {
        await this.api.put(this.api.storedObject('app-user', 'ui/tables/settings.json', this.currentUserConfig));
    }


    @debounce(250)
    protected async syncAdminConfig() {
        await this.api.put(this.api.storedObject('app-admin', 'ui/tables/admin.json', this.adminConfig));
    }

}

export interface IUserTableConfiguration {
    order: string[];
    hidden: string[];
}

export interface ITableConfiguration extends IUserTableConfiguration {
    locked: string[];
}

